export const PRIORITIES = ["Low", "Medium", "High", "Urgent"];

export const TASK_CATEGORIES = [
  "Bug",
  "New Development (Customer)",
  "New Development (CP)",
  "Enhancement New",
  "Customer Installation",
  "New to do",
];

export const CATEGORY_STYLES = {
  Bug: "bg-red-100 text-red-700",
  "New Development (Customer)": "bg-purple-100 text-purple-700",
  "New Development (CP)": "bg-indigo-100 text-indigo-700",
  "Enhancement New": "bg-teal-100 text-teal-700",
  "Customer Installation": "bg-amber-100 text-amber-700",
  "New to do": "bg-slate-100 text-slate-700",
};

export const PRIORITY_STYLES = {
  Low: "bg-gray-100 text-gray-700",
  Medium: "bg-blue-100 text-blue-700",
  High: "bg-orange-100 text-orange-700",
  Urgent: "bg-red-100 text-red-700",
};

export const getAuthUser = () => {
  try {
    const auth = JSON.parse(localStorage.getItem("pocketbase_auth") || "null");
    return auth?.record || null;
  } catch {
    return null;
  }
};

/** Normalize PocketBase relation fields to an array of ids. */
export const getRelationIds = (value, expanded) => {
  if (Array.isArray(expanded)) {
    return expanded.map((item) => (typeof item === "string" ? item : item?.id)).filter(Boolean);
  }
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "string" && value) return [value];
  if (value && typeof value === "object" && value.id) return [value.id];
  if (expanded?.id) return [expanded.id];
  return [];
};

export const sameIdSet = (a = [], b = []) => {
  const left = [...a].sort().join(",");
  const right = [...b].sort().join(",");
  return left === right;
};

export const getExpandedAssignees = (taskOrExpanded) => {
  const expanded = Array.isArray(taskOrExpanded)
    ? taskOrExpanded
    : taskOrExpanded?.expand?.AssignedTo ?? taskOrExpanded;

  if (Array.isArray(expanded)) {
    return expanded.filter((contact) => isInternalContact(contact));
  }
  if (expanded?.id && isInternalContact(expanded)) {
    return [expanded];
  }
  return [];
};

export const filterValidAssigneeIds = (ids = [], contacts = []) =>
  ids.filter((id) => contacts.some((c) => c.id === id));

export const assigneesLabel = (taskOrContacts) => {
  const contacts = Array.isArray(taskOrContacts)
    ? taskOrContacts.filter(isInternalContact)
    : getExpandedAssignees(taskOrContacts);

  if (contacts.length === 0) return "Unassigned";
  return contacts.map((c) => contactLabel(c)).join(", ");
};

/** Normalize PocketBase relation fields (string id, array, or expanded record). */
export const getRelationId = (value) => getRelationIds(value)[0] || "";

export const contactLabel = (contact) => {
  if (!contact) return "Unassigned";
  return `${contact.Name || ""} ${contact.Surname || ""}`.trim() || contact.email || "Unknown";
};

export const isInternalContact = (contact) => contact?.isInternal === true;

/** Assignee display — hides non-internal contacts. */
export const assigneeLabel = (contact) => {
  if (!isInternalContact(contact)) return "Unassigned";
  return contactLabel(contact);
};

/** General internal-team label — returns null when contact is not internal. */
export const internalContactLabel = (contact) => {
  if (!isInternalContact(contact)) return null;
  return contactLabel(contact);
};

export const parseChecklist = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

export const generateChecklistId = () =>
  `chk_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

export const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
};

export const isOverdue = (dueDate, status) => {
  if (!dueDate || status?.IsTerminal) return false;
  const due = new Date(dueDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  return due < today;
};

const PRIORITY_RANK = {
  Urgent: 4,
  High: 3,
  Medium: 2,
  Low: 1,
};

const compareDates = (a, b, direction = 1) => {
  const dateA = a ? new Date(a).getTime() : 0;
  const dateB = b ? new Date(b).getTime() : 0;
  if (!dateA && !dateB) return 0;
  if (!dateA) return 1;
  if (!dateB) return -1;
  return (dateA - dateB) * direction;
};

/** Client-side sort for list view and board columns */
export const sortTasks = (tasks, sortOrder, statuses = []) => {
  const list = [...tasks];
  const statusOrder = Object.fromEntries(statuses.map((s, i) => [s.id, i]));

  const getStatusRank = (task) => {
    const statusId = getRelationId(task.Status) || getRelationId(task.expand?.Status);
    return statusOrder[statusId] ?? 999;
  };

  list.sort((a, b) => {
    switch (sortOrder) {
      case "+created":
        return new Date(a.created) - new Date(b.created);
      case "-created":
        return new Date(b.created) - new Date(a.created);
      case "+DueDate":
        return compareDates(a.DueDate, b.DueDate, 1);
      case "-DueDate":
        return compareDates(a.DueDate, b.DueDate, -1);
      case "+Priority":
        return (PRIORITY_RANK[a.Priority] || 0) - (PRIORITY_RANK[b.Priority] || 0);
      case "-Priority":
        return (PRIORITY_RANK[b.Priority] || 0) - (PRIORITY_RANK[a.Priority] || 0);
      case "-updated":
        return new Date(b.updated) - new Date(a.updated);
      case "+Position,+created":
      default: {
        const statusDiff = getStatusRank(a) - getStatusRank(b);
        if (statusDiff !== 0) return statusDiff;
        const posDiff = (Number(a.Position) || 0) - (Number(b.Position) || 0);
        if (posDiff !== 0) return posDiff;
        return new Date(a.created) - new Date(b.created);
      }
    }
  });

  return list;
};

/** Sort tasks within a single board column */
export const sortTasksInColumn = (tasks, sortOrder = "+Position,+created") => {
  const list = [...tasks];

  list.sort((a, b) => {
    switch (sortOrder) {
      case "+created":
        return new Date(a.created) - new Date(b.created);
      case "-created":
        return new Date(b.created) - new Date(a.created);
      case "+DueDate":
        return compareDates(a.DueDate, b.DueDate, 1);
      case "-DueDate":
        return compareDates(a.DueDate, b.DueDate, -1);
      case "+Priority":
        return (PRIORITY_RANK[a.Priority] || 0) - (PRIORITY_RANK[b.Priority] || 0);
      case "-Priority":
        return (PRIORITY_RANK[b.Priority] || 0) - (PRIORITY_RANK[a.Priority] || 0);
      case "-updated":
        return new Date(b.updated) - new Date(a.updated);
      case "+Position,+created":
      default: {
        const posDiff = (Number(a.Position) || 0) - (Number(b.Position) || 0);
        if (posDiff !== 0) return posDiff;
        return new Date(a.created) - new Date(b.created);
      }
    }
  });

  return list;
};
